"use client";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TicketIcon, Plus, Minus, EqualNot, QrCode } from "lucide-react";
import { useEffect, useState } from "react";
import Questions from "./questions";
import { useTranslations } from "next-intl";

export function Ticket({
  ticket,
  remainingQuantity,
  updateCartCallback,
  answers,
  setAnswers,
  ticketIndex,
  setAttendeesInfos,
  attendeeCopiedInfos,
  canChangeQuantity,
  hidePrice,
  isChildTicket = false,
  isCheckoutInstance = false,
  instanceIndex = 0,
  parentInstanceIndex = null, // New prop to track which parent instance this child belongs to
  ticketGlobalIndex = null,
}: any) {
  const t = useTranslations("General");
  const [quantity, setQuantity] = useState(0);

  useEffect(() => {
    updateCartCallback && updateCartCallback(ticket.id, quantity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity]);

  return (
    <>
      {remainingQuantity === 0 && (
        <>
          <Alert
            variant="destructive"
            className="bg-background/30 dark:text-white/50"
          >
            <EqualNot className="w-4 h-4" />
            <div className="flex justify-between">
              <AlertTitle>{ticket.name}</AlertTitle>
              {!hidePrice && (
                <AlertTitle className="text-lg font-semibold">
                  {ticket.price == 0 ? t("free") : ticket.price}
                  {ticket.price != 0 && <sup>TND</sup>}
                  <QrCode className="inline-block w-6 h-6 ml-3" />
                </AlertTitle>
              )}
            </div>
            <AlertDescription className="flex">
              <div className="grow">
                <div className="mr-6 grow">{ticket.description}</div>
              </div>
              <div className="flex justify-between w-20 text-nowrap h-fit ">
                <Badge
                  variant="destructive"
                  className="-ml-2 text-lg rotate-3 "
                >
                  Sold-out
                </Badge>
              </div>
            </AlertDescription>
          </Alert>
        </>
      )}

      {(remainingQuantity > 0 ||
        (!remainingQuantity && remainingQuantity !== 0)) && (
        <>
          <Alert
            variant={"unmuted"}
            className="z-10 flex flex-col gap-2 border-0 bg-background/50 "
          >
            <TicketIcon className="w-4 h-4" />
            <div className="flex flex-row items-center justify-between align-middle">
              <AlertTitle className="text-lg">{ticket.name}</AlertTitle>
              {!hidePrice && (
                <AlertTitle className="text-lg font-semibold">
                  {ticket.price == 0 ? t("free") : ticket.price}
                  {ticket.price != 0 && <sup>TND</sup>}
                  <QrCode className="inline-block w-6 h-6 ml-3" />
                </AlertTitle>
              )}
            </div>
            <AlertDescription className="z-10 flex ">
              <div className="mr-6 text-sm opacity-75 grow text-muted-foreground">
                {ticket.description}
              </div>
              {updateCartCallback && (
                <div className="flex items-center justify-between w-32 ">
                  <Button
                    data-umami-event={"➖ :  " + ticket.name}
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      let quantityStep = ticket.quantityStepPerOrder
                        ? ticket.quantityStepPerOrder
                        : (ticket.minPerOrder ?? 1);

                      let newQuantity = quantity - quantityStep;

                      if (
                        ticket.minPerOrder &&
                        quantity === ticket.minPerOrder
                      ) {
                        newQuantity = 0;
                      } else if (ticket.minPerOrder) {
                        newQuantity = Math.max(newQuantity, ticket.minPerOrder);
                      }

                      newQuantity = Math.max(0, newQuantity);
                      newQuantity = newQuantity - (newQuantity % quantityStep);

                      setQuantity(newQuantity);
                      updateCartCallback();
                    }}
                  >
                    <Minus
                      className="w-4 h-4"
                      data-umami-event={"➖ :  " + ticket.name}
                    />
                  </Button>
                  <span className="mx-4 text-xl text-muted-foreground">
                    {quantity}
                  </span>
                  <Button
                    data-umami-event={"➕ :  " + ticket.name}
                    variant="outline"
                    size="icon"
                    disabled={canChangeQuantity === false}
                    onClick={() => {
                      let quantityStep = ticket.quantityStepPerOrder
                        ? ticket.quantityStepPerOrder
                        : (ticket.minPerOrder ?? 1);

                      if (ticket.minPerOrder && quantity === 0) {
                        quantityStep = ticket.minPerOrder;
                      }

                      let newQuantity = quantity + quantityStep;

                      if (ticket.maxPerOrder) {
                        newQuantity = Math.min(ticket.maxPerOrder, newQuantity);
                      }

                      newQuantity = Math.min(remainingQuantity, newQuantity);
                      newQuantity = newQuantity - (newQuantity % quantityStep);

                      setQuantity(newQuantity);
                      updateCartCallback();
                    }}
                  >
                    <Plus
                      className="w-4 h-4"
                      data-umami-event={"➕ :  " + ticket.name}
                    />
                  </Button>
                </div>
              )}
            </AlertDescription>
            {!updateCartCallback && !isChildTicket && (
              <Questions
                questions={ticket.questions}
                answers={answers}
                setAnswers={setAnswers}
                setAttendeesInfos={setAttendeesInfos}
                ticketId={ticket.id}
                ticketIndex={ticketIndex}
                attendeeCopiedInfos={attendeeCopiedInfos}
                isChildTicket={isChildTicket}
                isCheckoutInstance={isCheckoutInstance}
                instanceIndex={instanceIndex}
                parentInstanceIndex={parentInstanceIndex}
                parentTicketId={ticket.parentTicketId}
                ticketGlobalIndex={ticketGlobalIndex}
              />
            )}
          </Alert>
        </>
      )}
    </>
  );
}

export default Ticket;
